import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const TaxSettings = () => {
  const [taxRates, setTaxRates] = useState([
    {
      id: 1,
      name: 'Standard VAT',
      type: 'VAT',
      rate: 20.0,
      region: 'United Kingdom',
      category: 'Standard',
      isDefault: true,
      status: 'Active'
    },
    {
      id: 2,
      name: 'Reduced VAT',
      type: 'VAT',
      rate: 5.0,
      region: 'United Kingdom',
      category: 'Reduced',
      isDefault: false,
      status: 'Active'
    },
    {
      id: 3,
      name: 'Sales Tax NY',
      type: 'Sales Tax',
      rate: 8.25,
      region: 'New York, USA',
      category: 'Standard',
      isDefault: false,
      status: 'Active'
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [actionStatus, setActionStatus] = useState({ type: '', message: '' });
  const [newTaxRate, setNewTaxRate] = useState({
    name: '',
    type: 'VAT',
    rate: 0,
    region: '',
    category: 'Standard',
    isDefault: false,
    status: 'Active'
  });

  const [taxSettings, setTaxSettings] = useState({
    enableAutomaticCalculation: true,
    inclusiveTaxDefault: false,
    roundingMethod: 'standard',
    displayTaxInclusive: false,
    compoundTax: false,
    reverseCharge: false,
    taxExemptionTracking: true
  });

  const [isSettingsModified, setIsSettingsModified] = useState(false);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const taxTypes = ['VAT', 'Sales Tax', 'GST', 'HST', 'PST', 'QST', 'Service Tax'];
  const taxCategories = ['Standard', 'Reduced', 'Zero Rate', 'Exempt'];
  const roundingMethods = ['Standard', 'Round Up', 'Round Down', 'No Rounding'];

  const validateTaxRate = () => {
    const errors = [];
    
    if (!newTaxRate?.name?.trim()) {
      errors?.push('Tax name is required');
    }
    
    if (newTaxRate?.rate < 0 || newTaxRate?.rate > 100) {
      errors?.push('Tax rate must be between 0 and 100');
    }
    
    if (!newTaxRate?.region?.trim()) {
      errors?.push('Region is required');
    }

    // Check for duplicate tax names (excluding current editing tax)
    const existingTax = taxRates?.find(tax => 
      tax?.name?.toLowerCase() === newTaxRate?.name?.toLowerCase() && 
      tax?.id !== editingTax?.id
    );
    if (existingTax) {
      errors?.push('Tax rate with this name already exists');
    }

    return errors;
  };

  const handleAddTaxRate = async () => {
    const validationErrors = validateTaxRate();
    
    if (validationErrors?.length > 0) {
      setActionStatus({ 
        type: 'error', 
        message: validationErrors?.join(', ') 
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const taxRate = {
        ...newTaxRate,
        id: Math.max(...taxRates?.map(t => t?.id), 0) + 1
      };
      
      setTaxRates(prev => [...prev, taxRate]);
      resetForm();
      
      setActionStatus({ 
        type: 'success', 
        message: `Tax rate "${taxRate?.name}" added successfully!` 
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to add tax rate. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTaxRate = async () => {
    const validationErrors = validateTaxRate();
    
    if (validationErrors?.length > 0) {
      setActionStatus({ 
        type: 'error', 
        message: validationErrors?.join(', ') 
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setTaxRates(prev => 
        prev?.map(tax => 
          tax?.id === editingTax?.id ? { ...editingTax, ...newTaxRate } : tax
        )
      );
      resetForm();
      
      setActionStatus({ 
        type: 'success', 
        message: `Tax rate "${newTaxRate?.name}" updated successfully!` 
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to update tax rate. Please try again.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTaxRate = async (taxId) => {
    const tax = taxRates?.find(t => t?.id === taxId);
    if (!tax) return;

    if (tax?.isDefault) {
      setActionStatus({ 
        type: 'error', 
        message: 'Cannot delete the default tax rate. Please set another tax rate as default first.' 
      });
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete "${tax?.name}"? This action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setTaxRates(prev => prev?.filter(tax => tax?.id !== taxId));
      
      setActionStatus({ 
        type: 'success', 
        message: `Tax rate "${tax?.name}" deleted successfully!` 
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to delete tax rate' 
      });
    }
  };

  const resetForm = () => {
    setNewTaxRate({
      name: '',
      type: 'VAT',
      rate: 0,
      region: '',
      category: 'Standard',
      isDefault: false,
      status: 'Active'
    });
    setEditingTax(null);
    setShowAddModal(false);
    setActionStatus({ type: '', message: '' });
  };

  const handleEditTaxRate = (tax) => {
    setEditingTax(tax);
    setNewTaxRate({
      name: tax?.name,
      type: tax?.type,
      rate: tax?.rate,
      region: tax?.region,
      category: tax?.category,
      isDefault: tax?.isDefault,
      status: tax?.status
    });
    setShowAddModal(true);
    setActionStatus({ type: '', message: '' });
  };

  const setAsDefault = async (taxId) => {
    const tax = taxRates?.find(t => t?.id === taxId);
    if (!tax) return;

    const confirmed = window.confirm(
      `Set "${tax?.name}" as the default tax rate?`
    );
    if (!confirmed) return;

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setTaxRates(prev => 
        prev?.map(tax => ({
          ...tax,
          isDefault: tax?.id === taxId
        }))
      );
      
      setActionStatus({ 
        type: 'success', 
        message: `"${tax?.name}" is now the default tax rate` 
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to set default tax rate' 
      });
    }
  };

  const handleSettingChange = (key, value) => {
    setTaxSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setIsSettingsModified(true);
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setActionStatus({ 
        type: 'success', 
        message: 'Tax settings saved successfully!' 
      });
      setIsSettingsModified(false);

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to save tax settings' 
      });
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleGenerateReport = async (reportType) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setActionStatus({ 
        type: 'success', 
        message: `${reportType} report generated successfully!` 
      });

      setTimeout(() => {
        setActionStatus({ type: '', message: '' });
      }, 3000);

    } catch (error) {
      setActionStatus({ 
        type: 'error', 
        message: `Failed to generate ${reportType} report` 
      });
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Tax Settings</h2>
          <p className="text-muted-foreground">Configure tax rates, calculation methods, and compliance settings</p>
        </div>
        <div className="flex items-center space-x-3">
          {isSettingsModified && (
            <span className="text-sm text-yellow-600 dark:text-yellow-400">
              Settings modified
            </span>
          )}
          <Button 
            onClick={() => setShowAddModal(true)} 
            iconName="Plus"
          >
            Add Tax Rate
          </Button>
        </div>
      </div>
      {/* Status Messages */}
      {actionStatus?.message && (
        <div className={`p-4 rounded-lg border ${
          actionStatus?.type === 'success' ?'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400' :'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
        }`}>
          <div className="flex items-center space-x-2">
            <Icon 
              name={actionStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
              size={16} 
            />
            <span className="text-sm font-medium">{actionStatus?.message}</span>
          </div>
        </div>
      )}
      {/* Tax Configuration */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-medium text-foreground">Tax Configuration</h3>
          <Button 
            onClick={handleSaveSettings} 
            iconName="Save"
            disabled={!isSettingsModified || isSavingSettings}
            className={isSavingSettings ? 'opacity-50' : ''}
          >
            {isSavingSettings ? (
              <>
                <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                Saving...
              </>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Automatic Tax Calculation</span>
                <p className="text-xs text-muted-foreground">Automatically calculate taxes on transactions</p>
              </div>
              <input
                type="checkbox"
                checked={taxSettings?.enableAutomaticCalculation}
                onChange={(e) => handleSettingChange('enableAutomaticCalculation', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Tax Inclusive Pricing</span>
                <p className="text-xs text-muted-foreground">Prices include tax by default</p>
              </div>
              <input
                type="checkbox"
                checked={taxSettings?.inclusiveTaxDefault}
                onChange={(e) => handleSettingChange('inclusiveTaxDefault', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Display Tax Inclusive</span>
                <p className="text-xs text-muted-foreground">Show tax-inclusive amounts in UI</p>
              </div>
              <input
                type="checkbox"
                checked={taxSettings?.displayTaxInclusive}
                onChange={(e) => handleSettingChange('displayTaxInclusive', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>
          </div>

          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Compound Tax</span>
                <p className="text-xs text-muted-foreground">Enable compound tax calculation</p>
              </div>
              <input
                type="checkbox"
                checked={taxSettings?.compoundTax}
                onChange={(e) => handleSettingChange('compoundTax', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Reverse Charge</span>
                <p className="text-xs text-muted-foreground">Enable reverse charge mechanism</p>
              </div>
              <input
                type="checkbox"
                checked={taxSettings?.reverseCharge}
                onChange={(e) => handleSettingChange('reverseCharge', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <span className="text-sm font-medium text-foreground">Tax Exemption Tracking</span>
                <p className="text-xs text-muted-foreground">Track tax-exempt transactions</p>
              </div>
              <input
                type="checkbox"
                checked={taxSettings?.taxExemptionTracking}
                onChange={(e) => handleSettingChange('taxExemptionTracking', e?.target?.checked)}
                className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
              />
            </label>

            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">
                Rounding Method
              </label>
              <select
                value={taxSettings?.roundingMethod}
                onChange={(e) => handleSettingChange('roundingMethod', e?.target?.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {roundingMethods?.map(method => (
                  <option key={method} value={method?.toLowerCase()?.replace(' ', '_')}>{method}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      {/* Tax Rates */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Tax Rates</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-foreground">Name</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Type</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Rate</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Region</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Category</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Status</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {taxRates?.map((tax) => (
                <tr key={tax?.id} className="border-t border-border hover:bg-muted/20">
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-foreground">{tax?.name}</span>
                      {tax?.isDefault && (
                        <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                          Default
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="px-2 py-1 text-xs font-medium bg-muted text-foreground rounded">
                      {tax?.type}
                    </span>
                  </td>
                  <td className="p-3">
                    <span className="font-medium text-foreground">{tax?.rate}%</span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-foreground">{tax?.region}</span>
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-foreground">{tax?.category}</span>
                  </td>
                  <td className="p-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      tax?.status === 'Active' ?'bg-green-100 text-green-700' :'bg-red-100 text-red-700'
                    }`}>
                      {tax?.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      {!tax?.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAsDefault(tax?.id)}
                          iconName="Star"
                        >
                          Set Default
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditTaxRate(tax)}
                        iconName="Edit"
                      >
                        Edit
                      </Button>
                      {!tax?.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteTaxRate(tax?.id)}
                          iconName="Trash2"
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Tax Reports */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Tax Reporting</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="FileText" size={24} className="mx-auto mb-2 text-primary" />
            <h4 className="font-medium text-foreground mb-1">VAT Return</h4>
            <p className="text-sm text-muted-foreground mb-3">Generate VAT return reports</p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleGenerateReport('VAT Return')}
            >
              Generate
            </Button>
          </div>

          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="BarChart3" size={24} className="mx-auto mb-2 text-primary" />
            <h4 className="font-medium text-foreground mb-1">Tax Summary</h4>
            <p className="text-sm text-muted-foreground mb-3">View tax collection summary</p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleGenerateReport('Tax Summary')}
            >
              View
            </Button>
          </div>

          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="Download" size={24} className="mx-auto mb-2 text-primary" />
            <h4 className="font-medium text-foreground mb-1">Export Data</h4>
            <p className="text-sm text-muted-foreground mb-3">Export tax data for filing</p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleGenerateReport('Tax Data Export')}
            >
              Export
            </Button>
          </div>
        </div>
      </div>
      {/* Add/Edit Tax Rate Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                {editingTax ? 'Edit Tax Rate' : 'Add New Tax Rate'}
              </h3>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={resetForm}
                disabled={isLoading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>

            {/* Modal Status Messages */}
            {actionStatus?.message && showAddModal && (
              <div className={`mx-6 mt-4 p-3 rounded-lg border ${
                actionStatus?.type === 'success' ?'bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400' :'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400'
              }`}>
                <div className="flex items-center space-x-2">
                  <Icon 
                    name={actionStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
                    size={16} 
                  />
                  <span className="text-sm">{actionStatus?.message}</span>
                </div>
              </div>
            )}

            <div className="p-6 space-y-4">
              <Input
                label="Tax Name"
                value={newTaxRate?.name}
                onChange={(e) => setNewTaxRate(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder="Enter tax name"
                required
                disabled={isLoading}
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Tax Type
                </label>
                <select
                  value={newTaxRate?.type}
                  onChange={(e) => setNewTaxRate(prev => ({ ...prev, type: e?.target?.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
                  disabled={isLoading}
                >
                  {taxTypes?.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <Input
                label="Tax Rate (%)"
                type="number"
                value={newTaxRate?.rate}
                onChange={(e) => setNewTaxRate(prev => ({ ...prev, rate: parseFloat(e?.target?.value) || 0 }))}
                placeholder="0.00"
                step="0.01"
                min="0"
                max="100"
                required
                disabled={isLoading}
              />

              <Input
                label="Region"
                value={newTaxRate?.region}
                onChange={(e) => setNewTaxRate(prev => ({ ...prev, region: e?.target?.value }))}
                placeholder="Enter region/jurisdiction"
                required
                disabled={isLoading}
              />

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Category
                </label>
                <select
                  value={newTaxRate?.category}
                  onChange={(e) => setNewTaxRate(prev => ({ ...prev, category: e?.target?.value }))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
                  disabled={isLoading}
                >
                  {taxCategories?.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newTaxRate?.isDefault}
                  onChange={(e) => setNewTaxRate(prev => ({ ...prev, isDefault: e?.target?.checked }))}
                  className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
                  disabled={isLoading}
                />
                <span className="text-sm text-foreground">Set as default tax rate</span>
              </label>
            </div>

            <div className="flex justify-end space-x-4 p-6 border-t border-border">
              <Button 
                variant="outline" 
                onClick={resetForm}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={editingTax ? handleUpdateTaxRate : handleAddTaxRate}
                iconName="Save"
                disabled={isLoading}
                className={isLoading ? 'opacity-50' : ''}
              >
                {isLoading ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin mr-2" />
                    {editingTax ? 'Updating...' : 'Adding...'}
                  </>
                ) : (
                  editingTax ? 'Update Tax Rate' : 'Add Tax Rate'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxSettings;